import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { validateLogin } from "../../services/validate-login";
import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import { getFormattedDate } from "../../utils/dateFormat";

function Form() {
  const [fName, setFName] = useState(localStorage.getItem("firstName")??"");
  const [mName, setMName] = useState(localStorage.getItem("middleName")??"");
  const [lName, setLName] = useState(localStorage.getItem("lastName")??"");
  const [gender, setGender] = useState(localStorage.getItem("gender")??"MALE");
  const [pan, setPan] = useState(localStorage.getItem("pan")??"");
  const [dob, setDob] = useState(localStorage.getItem("dob")??"");
  const [personalEmail, setPersonalEmail] = useState(localStorage.getItem("email")??"");
  const [contactNumber, setContactNumber] = useState(localStorage.getItem("contactNumber")??"");
  const [address1, setAddress1] = useState(localStorage.getItem("addressL1")??"");
  const [address2, setAddress2] = useState(localStorage.getItem("addressL2")??"");
  const [pinCode, setPinCode] = useState(localStorage.getItem("pincode")??"");
  const [district, setDistrict] = useState(localStorage.getItem("city")??"");
  const [state, setState] = useState(localStorage.getItem("state")??"");
  const [formIsComplete, setFormIsComplete] = useState(false); 
  const [panError, setPanError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [dobError, setDobError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    async function getStep() {
      if(Number(localStorage.getItem("step")) >= 1){checkForm(); localStorage.setItem("step","1")} else{ navigate("/personal-loan") }
    }
    getStep();
  }, []);

  const validatePAN = (value: string) => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(value)) {
      setPanError("Invalid PAN format (e.g., ABCDE1234F)");
    } else {
      setPanError("");
    }
  };

  const fetchPinDetails = async (pincode: string) => {
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();
      setDistrict(data[0].PostOffice[0].District)
      setState(data[0].PostOffice[0].State)
    } catch (error) {
      console.error("Error fetching pincode details:", error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Only allow numeric values
    if (!/^\d*$/.test(value)) return;

    setPinCode(value);

    // Trigger API when 6 digits are entered
    if (value.length === 6) {
      fetchPinDetails(value);
    }
  };

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(value)) {
      setEmailError("Invalid email format (e.g., example@mail.com)");
    } else {
      setEmailError("");
    }
  };

  const validateContact = (value: string) => {
    const contactRegex = /^[6-9]\d{9}$/;
    if (!contactRegex.test(value)) {
      setPhoneError("Enter a valid 10-digit mobile number starting with 6-9");
    } else {
      setPhoneError("");
    }
  };

  useEffect(() => {
    async function getStep() {
      if(Number(localStorage.getItem("step")) >= 1){} else{ navigate("/personal-loan") }
    }
    getStep();
  }, []);

  const checkForm = () => {
    if(fName != "" && gender != "" && pan != "" && dob != "" && personalEmail != "" && contactNumber.length >= 10 && address1 != "" && address2 != "" && pinCode.length >= 6){
      setFormIsComplete(true)
    }else{
      setFormIsComplete(false)
    }
  }

    const checkForm1 = () => {
    if (!dob) return;

    const today = new Date();
    const minDate = new Date(
      today.getFullYear() - 21,
      today.getMonth(),
      today.getDate()
    );

    const selectedDate = new Date(dob);

    if (selectedDate > minDate) {
      setDobError("Age must be at least 21 years.");
      setFormIsComplete(false)
    } else {
      setDobError("");
    }
  };

  const onClickEvent = () => {
    localStorage.setItem("firstName", fName)
    localStorage.setItem("middleName", mName)
    localStorage.setItem("lastName", lName)
    localStorage.setItem("email", personalEmail)
    localStorage.setItem("dob", dob)
    localStorage.setItem("gender", gender)
    localStorage.setItem("pan", pan)
    localStorage.setItem("contactNumber", contactNumber)
    localStorage.setItem("addressL1", address1)
    localStorage.setItem("addressL2", address2)
    localStorage.setItem("city", district)
    localStorage.setItem("state", state)
    localStorage.setItem("pincode", pinCode)
    localStorage.setItem("step1TimeStamp", getFormattedDate())
    localStorage.setItem("step", "2")
    navigate("/employment-details")
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#f5f9ff] px-4">
      {/* Hero Section */}
      <div className="flex flex-col md:flex-row max-w-5xl w-full bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Left side Image (fixed) */}
        <div className="basis-full md:basis-2/5 flex items-center justify-center p-6 bg-gray-50 md:h-[calc(100vh-100px)] md:sticky md:top-0">
          <img
            src="svg/img2.svg"
            alt="Working professional"
            className="w-full max-w-xs md:max-w-none object-contain"
          />
        </div>

        {/* Right side Form (scrollable) */}
        <div className="basis-full md:basis-3/5 flex flex-col p-8 bg-white relative md:h-[calc(100vh-100px)] md:overflow-y-auto">
          {/* Step Number */}
          <div className="absolute top-4 right-4 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
            Step 2
          </div>
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            <h2 className="text-xl font-semibold mb-6">PERSONAL LOAN APPLICATION</h2>
            <p className="text-sm text-gray-600 mb-4">
              Please enter your information.<br></br>
              <b>Caution:</b> <i>Incorrect or information mis-match will lead to termination of Loan application</i>
            </p>

            {/* Extra Fields */}
            <div className="space-y-6">
              {/* First Name Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={fName}
                  onKeyUp={(e) => checkForm()}
                  onChange={(e) => setFName(e.target.value)}
                  placeholder="As per PAN Database"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Middle Name Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Middle Name
                </label>
                <input
                  type="text"
                  value={mName}
                  onKeyUp={(e) => checkForm()}
                  onChange={(e) => setMName(e.target.value)}
                  placeholder="As per PAN Database"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Last Name Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lName}
                  onKeyUp={(e) => checkForm()}
                  onChange={(e) => setLName(e.target.value)}
                  placeholder="As per PAN Database"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">Gender</label>
                <select
                  value={gender}
                  onKeyUp={(e) => checkForm()}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="TRANSGENDER">Transgender</option>
                </select>
              </div>

              {/* PAN */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  PAN
                </label>
                <input
                  type="text"
                  value={pan}
                  onKeyUp={(e) => checkForm()}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase(); // auto uppercase
                    setPan(val);
                    validatePAN(val);
                  }}
                  placeholder="As per PAN Database"
                  className={`w-full border rounded-lg px-4 py-2 focus:ring-2 outline-none ${
                    panError ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {panError && <p className="text-red-500 text-sm mt-1">{panError}</p>}
              </div>

              {/* Date Picker */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">Date of Birth</label>
                <input
                  type="date"
                  value={dob}
                  onKeyUp={checkForm1}
                  onChange={(e) => {
                    setDob(e.target.value);
                    checkForm1();
                  }}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                />
                {dobError && <p className="text-red-500 text-sm mt-1">{dobError}</p>}
              </div>

              {/* Personal Email */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Personal Email
                </label>
                <input
                  type="email"
                  value={personalEmail}
                  onKeyUp={(e) => checkForm()}
                  onChange={(e) => {
                    const val = e.target.value.trim();
                    setPersonalEmail(val);
                    validateEmail(val);
                  }}
                  placeholder="Your email for communication"
                  className={`w-full border rounded-lg px-4 py-2 focus:ring-2 outline-none ${
                    emailError ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {emailError && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
              </div>

              {/* Phone Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Contact
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <span className="px-3 text-gray-600">+91</span>
                  <input
                    type="number"
                    value={contactNumber}
                    onKeyUp={(e) => checkForm()}
                    onChange={(e) => {
                      // allow only digits
                      const val = e.target.value.replace(/\D/g, "");
                      setContactNumber(val);
                      validateContact(val);
                    }}
                    maxLength={10}
                    placeholder="Mobile number linked to AADHAAR & PAN"
                    className="w-full px-4 py-2 rounded-r-lg outline-none border-0"
                  />
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  <i>Make sure AADHAAR and PAN numbers are linked to this Mobile number before applying for a loan. If not linked, your loan application will be rejected</i>
                </p>
                {phoneError && <p className="text-red-500 text-sm mt-1">{phoneError}</p>}
              </div>

              {/* Address 1 */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Address 1
                </label>
                <input
                  type="text"
                  value={address1}
                  onKeyUp={(e) => checkForm()}
                  onKeyDown={(e) => checkForm()}
                  onChange={(e) => setAddress1(e.target.value)}
                  placeholder="As per AADHAAR"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Address 2 */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Address 2
                </label>
                <input
                  type="text"
                  value={address2}
                  onKeyUp={(e) => checkForm()}
                  onChange={(e) => setAddress2(e.target.value)}
                  placeholder="As per AADHAAR"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Pincode Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">Pin Code</label>
                <input
                  type="text"
                  maxLength={6} // restrict to 6 digits
                  value={pinCode}
                  onKeyUp={(e) => checkForm()}
                  onChange={handleChange}
                  placeholder="As per AADHAAR"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* District */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  District
                </label>
                <input
                  type="text"
                  value={district}
                  readOnly
                  // onKeyUp={(e) => checkForm()}
                  // onChange={(e) => setDistrict(e.target.value)}
                  // placeholder="As per PAN Database"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* State */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={state}
                  readOnly
                  // onKeyUp={(e) => checkForm()}
                  // onChange={(e) => setState(e.target.value)}
                  // placeholder="As per PAN Database"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Next Buttons in a row */}
          <div className="mt-6 flex gap-4">
            <button
              className="flex-1 bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
              onClick={() => navigate("/personal-loan")}
            >
            <ArrowLeft size={18} /> Back 
            </button>

            <button
              className={`flex-1 px-8 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition
                ${formIsComplete
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-400 cursor-not-allowed"}`}
              onClick={onClickEvent}
              disabled={!formIsComplete}
            >
              Next <ArrowRight size={18} />
            </button>
          </div>

        </div>
      </div>

      {/* Footer Stats */}
      <Footer />
    </div>
  );
}

export default function Step2BasicDetails() {
  const navigate = useNavigate();

  useEffect(() => {
    validateLogin(navigate);
  }, [navigate]);

  return (
    <div className="flex flex-col h-screen overflow-y-auto">
      <div className="flex-shrink-0">
        <Header title="" />
      </div>

      {/* Main Content scrollable */}
      <main className="flex-1 overflow-y-auto">
          <Form />
      </main>
    </div>
  );
}
